import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const RECENT_KEY = 'rk_recent_visited';
const MAX_ITEMS = 5;

const TRACKED_PATTERNS = [
  { pattern: /^\/dashboard\/catalogue\/(.+)/, label: (m) => {
    // real_product_name_28oct2026 (CEO): never show 'Product Detail'
    try { return sessionStorage.getItem(`rvk_pname_${m[1]}`) || 'Product Catalogue'; }
    catch { return 'Product Catalogue'; }
  } },
  { pattern: /^\/dashboard\/catalogue$/, label: () => 'Product Catalogue' },
  { pattern: /^\/dashboard\/orders$/, label: () => 'Orders' },
  { pattern: /^\/dashboard\/cart$/, label: () => 'Cart' },
  { pattern: /^\/dashboard\/stock$/, label: () => 'Stock Management' },
  { pattern: /^\/dashboard\/stock\/reconciliation$/, label: () => 'Stock Reconciliation' },
  { pattern: /^\/dashboard\/stock\/scanner$/, label: () => 'Barcode Scanner' },
  { pattern: /^\/dashboard\/revenue$/, label: () => 'Revenue Tracker' },
  { pattern: /^\/dashboard\/verification-hub$/, label: () => 'Verification Hub' },
  { pattern: /^\/dashboard\/verification$/, label: () => 'Verification Queue' },
  { pattern: /^\/dashboard\/crm$/, label: () => 'Prospect CRM' },
  { pattern: /^\/dashboard\/salon-registry$/, label: () => 'Salon Registry' },
  { pattern: /^\/dashboard\/messages$/, label: () => 'Messages' },
  { pattern: /^\/dashboard\/learning$/, label: () => 'Learning' },
  { pattern: /^\/dashboard\/training$/, label: () => 'Training' },
  { pattern: /^\/dashboard\/gallery$/, label: () => 'Gallery' },
  { pattern: /^\/dashboard\/rewards$/, label: () => 'Rewards' },
  { pattern: /^\/dashboard\/calculator$/, label: () => 'Profit Calculator' },
  { pattern: /^\/dashboard\/leaderboard$/, label: () => 'Leaderboard' },
  { pattern: /^\/dashboard\/pro-status$/, label: () => 'Pro Status' },
  { pattern: /^\/dashboard\/visits$/, label: () => 'Visit Tracking' },
  { pattern: /^\/dashboard\/security$/, label: () => 'Security Settings' },
  { pattern: /^\/dashboard\/profile$/, label: () => 'My Profile' },
  { pattern: /^\/dashboard\/payroll$/, label: () => 'Payroll' },
  { pattern: /^\/dashboard\/weekly-digest$/, label: () => 'Weekly Digest' },
  { pattern: /^\/dashboard\/sales-funnel$/, label: () => 'Sales Funnel' },
  { pattern: /^\/dashboard\/salon-settings$/, label: () => 'Salon Settings' },
  { pattern: /^\/dashboard\/about$/, label: () => 'About' },
  { pattern: /^\/dashboard\/forecast$/, label: () => 'Demand Forecast' },
  { pattern: /^\/dashboard\/starter-packs$/, label: () => 'Starter Packs' },
  { pattern: /^\/dashboard\/business-planner$/, label: () => 'Business Planner' },
  { pattern: /^\/dashboard\/ceo-command$/, label: () => 'CEO Command' },
  { pattern: /^\/dashboard\/salon-performance$/, label: () => 'Salon Performance' },
  { pattern: /^\/dashboard\/subscriptions$/, label: () => 'Subscriptions' },
];

const TAB_LABELS = {
  'invoices': 'Invoices', 'payments': 'Payments', 'dispatch': 'Dispatch',
  'revenue': 'Revenue Dashboard', 'sales-command': 'Sales Command',
  'onboarding': 'Onboarding', 'salon-directory': 'Salon Directory',
  'products': 'Products', 'users': 'Users', 'financial': 'Financial',
  'cleanup': 'Cleanup Tool', 'verifications': 'Verifications',
  'leads': 'Leads', 'orders': 'Orders',
};

function getStored() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}

function setStored(items) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, MAX_ITEMS))); } catch {}
}

export function useRecentPages() {
  const location = useLocation();
  const [recent, setRecent] = useState(getStored);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/login' || path === '/register' || path === '/') return;

    // Delivery agents: do not track recently visited
    try {
      const raw = localStorage.getItem('rk_auth_user');
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.role === 'delivery_agent') return;
      }
    } catch {}

    let label = null;
    for (const { pattern, label: labelFn } of TRACKED_PATTERNS) {
      const m = path.match(pattern);
      if (m) { label = labelFn(m); break; }
    }
    if (!label) return;

    const items = getStored().filter(p => p.path !== path);
    items.unshift({ path, label, ts: Date.now() });
    const trimmed = items.slice(0, MAX_ITEMS);
    setStored(trimmed);
    setRecent(trimmed);
  }, [location.pathname]);

  return recent;
}

export function trackTabVisit(tabKey) {
  const label = TAB_LABELS[tabKey];
  if (!label) return;
  const path = `/dashboard#${tabKey}`;
  const items = getStored().filter(p => p.path !== path);
  items.unshift({ path, label, ts: Date.now(), isTab: true, tabKey });
  setStored(items.slice(0, MAX_ITEMS));
}

export function getRecentItems() {
  return getStored();
}
